import React, {FC, useCallback, useState} from 'react';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {useInitialEffect} from '../../../../../libs/shared/core/src/helpers/react/hooks/useEffectHelpers';
import {
  ReplyFactModel,
  ReplyFactReferenceModel,
} from '../../../../../libs/shared/core/src/models/api/factModel';
import {MessageModel} from '../../../../../libs/shared/core/src/models/api/messageModel';
import {DataEventActionsEnum} from '../../../../../libs/shared/core/src/models/tracking/dataEventActions';
import {ViewerDataEventObject} from '../../../../../libs/shared/core/src/models/tracking/objectNames/viewerDataEventObject';
import {useAppQuery} from '../../../../../libs/shared/core/src/services/query/useAppQuery';
import {useDataEventTracking} from '../../../../../libs/shared/core/src/services/tracking/dataEventTrackingContext';
import {
  InterfaceEventsEnum,
  ReplyLocationsEnum,
  trackInterfaceEvent,
} from '../../../../../libs/shared/core/src/services/tracking/tracking';
import {
  EmlPanelId,
  MessagePanelId,
  ViewerPanelId,
} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {openInBrowser} from '../../../../../srcCommon/browser/openInBrowser';
import {useAppDispatch} from '../../../../helpers/appReduxHelpers';
import {ReplyTypesEnum} from '../../../../helpers/models/recipientHelpers';
import {TagModel} from '../../../../models/api/tagModel';
import {tagQueries} from '../../../../queries/tagQueries';
import {markdownToHtml} from '../../../../services/converters/markdownToHtmlConverter';
import {ThunkAsyncAction} from '../../../../store/appThunk';
import {addTagToConversationsAction} from '../../../../store/data/conversations/tagConversationsActions';
import {
  ExistingDraftStrategiesEnum,
  replyToMessageAction,
} from '../../../../store/data/messages/draftMessagesActions';
import {AiReplyPreview} from './aiReplyPreview';
import {InheritableTriggerButtonProps, TriggerButton} from './triggerButton';

interface AiReplyButtonProps extends InheritableTriggerButtonProps {
  aiReply: ReplyFactModel;
  message: MessageModel;
  panelId: ViewerPanelId | MessagePanelId | EmlPanelId;
}

export const AiReplyButton: FC<AiReplyButtonProps> = ({aiReply, message, renderDropdown, panelId}) => {
  const [isRunning, setIsRunning] = useState(false);
  const dispatch = useAppDispatch();
  const {trackDataEvent} = useDataEventTracking();

  const {data: trackTag} = useAppQuery(tagQueries.id(aiReply.trackTagId));

  const title = isRunning ? (
    <Trans id="workflow-actions-generating-reply">Generating draft</Trans>
  ) : (
    <Trans id="workflow-actions-suggest-reply">Suggest reply</Trans>
  );

  useInitialEffect(() => {
    trackDataEvent(DataEventActionsEnum.VIEW, ViewerDataEventObject.AI_SUGGEST, {
      message_id: message.id,
      fact_id: aiReply.id,
    });
  });

  const onAcceptAiReply = useCallback(async () => {
    setIsRunning(true);

    try {
      await dispatch(acceptAiReply(aiReply, trackTag, message, panelId));
    } catch (err) {
      // TODO: error banner
    } finally {
      setIsRunning(false);
    }
  }, [dispatch, aiReply, trackTag, message, panelId]);

  return (
    <TriggerButton
      title={title}
      isLoading={isRunning}
      onSelect={() => {
        onAcceptAiReply();

        trackInterfaceEvent(InterfaceEventsEnum.P3_AI_SUGGEST_CLICK, {
          message_id: message.id,
          conversation_id: message.conversationId,
          fact_id: aiReply.id,
        });
      }}
      renderDropdown={renderDropdown}
      renderPreview={(anchor, popperPlacement) => (
        <AiReplyPreview
          anchor={anchor}
          popperPlacement={popperPlacement}
          aiReply={aiReply}
          onSourceClick={(reference: ReplyFactReferenceModel) => {
            openInBrowser(reference.href);

            trackInterfaceEvent(InterfaceEventsEnum.P3_AI_SUGGEST_SOURCE_CLICK, {
              message_id: message.id,
              conversation_id: message.conversationId,
              fact_id: aiReply.id,
            });
          }}
        />
      )}
      shouldUseAiIcon
      onViewPreview={(hoverTime) => {
        trackInterfaceEvent(InterfaceEventsEnum.P3_AI_SUGGEST_HOVER, {
          message_id: message.id,
          conversation_id: message.conversationId,
          fact_id: aiReply.id,
          hover_time: hoverTime,
        });
      }}
    />
  );
};

function acceptAiReply(
  aiReply: ReplyFactModel,
  trackTag: TagModel | undefined,
  message: MessageModel,
  panelId: ViewerPanelId | MessagePanelId | EmlPanelId,
): ThunkAsyncAction {
  return async (dispatch) => {
    if (trackTag) {
      dispatch(addTagToConversationsAction([message.conversationId], trackTag.id, [trackTag]));
    }

    dispatch(
      replyToMessageAction({
        panelId,
        replyType: ReplyTypesEnum.ALL,
        replyToUid: message.uid,
        conversationId: message.conversationId,
        location: ReplyLocationsEnum.AI_SUGGESTION,
        existingDraftStrategy: ExistingDraftStrategiesEnum.THROW_ERROR,
        overrides: {
          text: aiReply.content,
          trustedHtml: markdownToHtml(aiReply.content),
        },
      }),
    );
  };
}
